import { EPaymentPlan } from '@if/core/types'
import { formatCurrency, formatDate } from '@if/core/utils'
import { BodyText, StrongValue, SubsectionTitle } from '@if/mui'
import { CircularProgress, Stack } from '@mui/material'
import {
  formatComputed,
  FormatPaymentPlanForSummary,
  getCoverageTotal,
  getIfXpressTotalObligation,
  getMerchantFees,
  getSelectedPlan,
} from '@src/data/worksheet-selectors'
import { ComputeFundingResultDto } from '@src/types/ComputeIfXpressFundingResultDto'
import { CreditApplication } from '@src/types/CreditApplicationSchema'
import { Merchant } from '@src/types/Merchant'
import { useTranslation } from 'react-i18next'

interface RowProbs {
  label: string
  value: string
}

const RowStack = ({ label, value }: RowProbs) => {
  return (
    <Stack direction="row" justifyContent="space-between" spacing={2}>
      <BodyText>{label} :</BodyText>
      <BodyText>{value}</BodyText>
    </Stack>
  )
}

interface Props {
  creditApplication: CreditApplication
  amountRequested: number
  hasVariableInterest: boolean
  firstPaymentOn: string
  merchant: Merchant
  paymentPlanId: EPaymentPlan
  computedIfXpressFunding: ComputeFundingResultDto | null
  isComputing: boolean
  isAmountOutOfRange: boolean
}
const IfXpressFundingSummary = ({
  creditApplication,
  amountRequested,
  hasVariableInterest,
  firstPaymentOn,
  merchant,
  paymentPlanId,
  computedIfXpressFunding,
  isComputing,
  isAmountOutOfRange,
}: Props) => {
  const { t } = useTranslation()
  const totalObligation = getIfXpressTotalObligation(amountRequested, computedIfXpressFunding)
  const selectedPlan = getSelectedPlan(merchant, paymentPlanId)
  const mustDisplayInterests = merchant.showInterestRateOnMerchantDashboard
  const insuranceTotal = getCoverageTotal(computedIfXpressFunding)
  const isReady = Boolean(computedIfXpressFunding) || isAmountOutOfRange
  const computed = (format: (funding: ComputeFundingResultDto) => string) =>
    formatComputed(computedIfXpressFunding, format)

  return (
    <Stack direction="column" spacing={2}>
      <SubsectionTitle>{t('worksheet.fundingSummary')} :</SubsectionTitle>
      {isComputing && (
        <div style={{ textAlign: 'center' }}>
          <CircularProgress />
        </div>
      )}
      {!isReady && !isComputing && <BodyText>{t('worksheet.personalFundingInvalid')}</BodyText>}
      {isReady && (
        <>
          <RowStack label={t('worksheet.requestedAmount')} value={formatCurrency(amountRequested)} />
          {mustDisplayInterests && (
            <>
              <RowStack
                label={`${t('worksheet.interest')} (${
                  hasVariableInterest
                    ? FormatPaymentPlanForSummary(selectedPlan)
                    : `${creditApplication.finalCreditDecision.interestRate}%`
                })`}
                value={computed((funding) => formatCurrency(funding.totalInterestAmount))}
              />

              <RowStack
                label={
                  computedIfXpressFunding
                    ? `${t('worksheet.financeFee')} (${computedIfXpressFunding.lenderFeeRate}%)`
                    : t('worksheet.financeFee')
                }
                value={computed((funding) => formatCurrency(funding.lenderFee))}
              />

              {hasVariableInterest && (
                <RowStack
                  label={`${t('worksheet.merchantFee')} (${selectedPlan?.merchantFeeRate}%)`}
                  value={computed(() => getMerchantFees(selectedPlan?.merchantFeeRate, amountRequested))}
                />
              )}

              <RowStack label={t('worksheet.protectionPlan')} value={computed(() => formatCurrency(insuranceTotal))} />

              <RowStack
                label={t('worksheet.totalObligation')}
                value={computed(() => formatCurrency(totalObligation))}
              />

              <RowStack
                label={t('worksheet.annualPercentageRate')}
                value={computed((funding) => `${funding.effectiveRate}%`)}
              />
            </>
          )}

          <Stack direction="row" justifyContent="space-between" paddingTop={3}>
            <SubsectionTitle>{t('worksheet.paymentTitle')}</SubsectionTitle>
          </Stack>

          <RowStack
            label={t('worksheet.maxPmtAmount')}
            value={formatCurrency(creditApplication?.finalCreditDecision.maxPmtAmount)}
          />

          <RowStack label={t('worksheet.paymentFrequency')} value={t('common.monthly')} />

          <RowStack
            label={t('worksheet.monthlyPayment')}
            value={computed((funding) => formatCurrency(funding.paymentForFrequency))}
          />

          <RowStack label={t('worksheet.firstPaymentOn')} value={formatDate(firstPaymentOn)} />

          <Stack direction="row" justifyContent="space-between" paddingTop={3}>
            <StrongValue>{t('worksheet.amountToBePaid')} :</StrongValue>
            <BodyText>{formatCurrency(amountRequested ?? 0)}</BodyText>
          </Stack>
        </>
      )}
    </Stack>
  )
}

export default IfXpressFundingSummary
